import { OptionType } from '../options-common/options.model';
import { PricingPadLeg } from './options-pricing-pad-model';

/**
 * OPP ATM Distance Indicator (CR-20260430-001).
 *
 * Pure helper computing the user-facing display string for the OPP
 * ATM-distance indicator. Spec: docs/change-requests/CR-20260430-001/
 * requirements-spec.md.
 *
 * Anchor rule (FR-5): for call legs the lowest strike is the anchor;
 * for put legs the highest strike is the anchor. Strategy type is
 * irrelevant.
 *
 * Sign rule (FR-6): distance = side_factor * (spot - anchor) where
 * side_factor = +1 for puts, -1 for calls. Yields OTM = positive,
 * ITM = negative.
 *
 * Rounding (FR-7, ADR-1): JS-default Math.round (rounds half toward
 * positive infinity).
 *
 * Display (FR-8): bare integer; negative values prefixed with ASCII '-'.
 *
 * Mode composition:
 *   - 'Calls'        → single call-side number      (FR-9)
 *   - 'Puts'         → single put-side number       (FR-10)
 *   - 'Calls & Puts' → `<call> / <put>` (FR-11, call-first)
 *
 * Empty / unknown:
 *   - returns null when no strategy/optionType yet (ADR-3) → *ngIf hides
 *   - returns 'N/A' (FR-14) when strategy is loaded but value cannot be
 *     computed (no anchor of relevant side, no spot, etc.)
 *   - mixed mode with exactly one side missing legs → '<n> / N/A' or
 *     'N/A / <n>' (FR-15); both sides missing → 'N/A'.
 */

export type LegsOptionType = 'Calls' | 'Puts' | 'Calls & Puts';

const NA = 'N/A';

/**
 * Compute the indicator display string for the OPP ATM distance feature.
 *
 * @param legs        Raw legs of the active strategy (PricingPadStrategy.legs).
 * @param optionType  OPP option-type discriminator.
 * @param spot        Underlying last price (component.lastPx).
 * @param hasStrategy True once selectedPricingModel is set on the component.
 * @returns           Display string, 'N/A', or null. Never throws.
 */
export function computeAtmDistanceDisplay(
    legs: PricingPadLeg[] | undefined | null,
    optionType: LegsOptionType | undefined | null,
    spot: number | null | undefined,
    hasStrategy: boolean,
): string | null {

    // ADR-3 *ngIf gate: hide entirely until a strategy + optionType exist.
    if (!hasStrategy || !optionType) {
        return null;
    }

    // FR-14 amendment 2026-04-30: no legs at all → hide entirely (return
    // null), not 'N/A'. Other empty cases (no spot, no anchor of
    // relevant side) still render 'N/A' below.
    if (legs == null || legs.length === 0) {
        return null;
    }

    const callDist = computeSideDistance(legs, OptionType.Call, spot);
    const putDist = computeSideDistance(legs, OptionType.Put, spot);

    if (optionType === 'Calls') {
        return callDist === null ? NA : formatInt(callDist);
    }

    if (optionType === 'Puts') {
        return putDist === null ? NA : formatInt(putDist);
    }

    // 'Calls & Puts' — FR-11 amended 2026-04-30: render one number per
    // side that HAS legs. Side without legs is omitted entirely (no
    // 'N/A' placeholder, no separator).
    //   both sides → '<call> / <put>' (call-first per FR-11)
    //   call-only  → '<call>'
    //   put-only   → '<put>'
    //   neither    → 'N/A'
    if (callDist !== null && putDist !== null) {
        return `${formatInt(callDist)} / ${formatInt(putDist)}`;
    }
    if (callDist !== null) {
        return formatInt(callDist);
    }
    if (putDist !== null) {
        return formatInt(putDist);
    }
    return NA;
}

/**
 * Pick the anchor strike for one side and return the signed, rounded
 * integer distance, or null if the value cannot be computed.
 *
 * Call anchor = min strike among call legs.
 * Put anchor  = max strike among put legs.
 * side_factor = -1 for calls, +1 for puts.
 */
function computeSideDistance(
    legs: PricingPadLeg[] | undefined | null,
    side: OptionType,
    spot: number | null | undefined,
): number | null {

    if (legs == null || legs.length === 0) {
        return null;
    }
    if (spot == null || !isFinite(spot)) {
        return null;
    }

    const sideLegs = legs.filter(l =>
        l != null && l.optionType === side && isValidStrike(l.strike)
    );
    if (sideLegs.length === 0) {
        return null;
    }

    let anchor: number;
    if (side === OptionType.Call) {
        anchor = sideLegs.reduce((m, l) => l.strike < m ? l.strike : m, sideLegs[0].strike);
    } else { // OptionType.Put
        anchor = sideLegs.reduce((m, l) => l.strike > m ? l.strike : m, sideLegs[0].strike);
    }

    const sideFactor = side === OptionType.Put ? 1 : -1;
    const raw = sideFactor * (spot - anchor);

    // Defensive: any NaN/Infinity sneaking in returns null (NFR-3).
    if (!isFinite(raw)) {
        return null;
    }

    return Math.round(raw);
}

function isValidStrike(s: number | null | undefined): s is number {
    return s != null && isFinite(s) && s > 0;
}

/**
 * Integer → display string. Math.round already produced the integer;
 * String() handles negative sign with ASCII '-' per ADR-4.
 */
function formatInt(n: number): string {
    return String(n);
}
